import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import { connectorRequestHash } from '../../supabase/functions/_shared/connectorWriteSafety';
import { appleObject, appleUuid, isAppleCalendarActor, validateAppleCalendarProof,
  type AppleCalendarActor, type AppleCalendarProof, type AppleCalendarSealedReview,
  type AppleEventSnapshot } from '../../supabase/functions/_shared/appleCalendarWriteDomain';

type AgendaRow = Database['public']['Tables']['agenda_events']['Row'];
type ImportedRow = Pick<AgendaRow, 'id'|'user_id'|'source'|'external_provider'|'external_id'|'provider_etag'|'deleted_at'|'title'|'tag'|'start_at'|'end_at'|'location'|'notes'|'all_day_start'|'all_day_end'>;
type ImportedFields = Pick<ImportedRow, 'title'|'tag'|'start_at'|'end_at'|'location'|'notes'|'all_day_start'|'all_day_end'>;
const SELECT = 'id,user_id,source,external_provider,external_id,provider_etag,deleted_at,title,tag,start_at,end_at,location,notes,all_day_start,all_day_end';
const BASELINE = 'apple:v1:';
export interface AppleCalendarReceiptSyncResult {
  status: 'ok'|'conflict'|'pending';
  reconciled: boolean;
  reason: string;
}
export interface AppleCalendarReceiptSyncOptions {
  client: SupabaseClient<Database>;
  review: AppleCalendarSealedReview;
  proof: AppleCalendarProof;
  currentActor: AppleCalendarActor;
  /** Checks authenticated owner, captured current Auth SID and device identity. */
  assertCurrent: () => Promise<void>;
  /** Captured operation/view lease; it must stay false after revocation or unmount. */
  isCurrent: () => boolean;
  /** Only explicit receipt recovery may use a different current SID. */
  allowHistoricalRecovery?: boolean;
  now?: number;
}
class ReceiptSyncBoundaryError extends Error {}
const result = (status: AppleCalendarReceiptSyncResult['status'], reason: string): AppleCalendarReceiptSyncResult =>
  ({status, reconciled:status==='ok', reason});
function canonicalFields(row: ImportedFields): string {
  return JSON.stringify([row.title,row.tag,Date.parse(row.start_at),row.end_at===null?null:Date.parse(row.end_at),
    row.location,row.notes,row.all_day_start,row.all_day_end]);
}
async function fingerprint(row: ImportedFields): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256',new TextEncoder().encode(canonicalFields(row)));
  return Array.from(new Uint8Array(digest),byte=>byte.toString(16).padStart(2,'0')).join('');
}
function baselineHash(value: string|null): string|null {
  if(!value?.startsWith(BASELINE))return null;
  try {
    const parsed:unknown=JSON.parse(value.slice(BASELINE.length));
    return appleObject(parsed)&&(parsed.modified===null||typeof parsed.modified==='string')&&
      typeof parsed.hash==='string'&&/^[a-f0-9]{64}$/.test(parsed.hash)?parsed.hash:null;
  }catch{return null;}
}
/** EventKit and the existing importer identify an occurrence by native ID + start.
 * Civil all-day dates require the effective native zone, never the browser zone.
 * A skipped midnight has no safe import identity and stays pending. */
function midnight(date: string, zone: string): number {
  const nominal=Date.parse(date+'T00:00:00Z');
  const formatter=new Intl.DateTimeFormat('en-US',{timeZone:zone,year:'numeric',month:'2-digit',day:'2-digit',
    hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'});
  const local=(instant:number)=>{
    const parts=formatter.formatToParts(new Date(instant));
    const part=(type:string)=>parts.find(p=>p.type===type)?.value??'';
    return {date:part('year')+'-'+part('month')+'-'+part('day'),hour:part('hour'),minute:part('minute'),second:part('second'),
      asUtc:Date.parse(part('year')+'-'+part('month')+'-'+part('day')+'T'+part('hour')+':'+part('minute')+':'+part('second')+'Z')};
  };
  const candidates=new Set<number>();
  // Sampling both sides includes offset transitions and non-hour offsets.
  for(const hours of [-48,-24,-12,0,12,24,48]){
    const sample=nominal+hours*3_600_000;
    const offset=local(sample).asUtc-sample;
    const candidate=nominal-offset,parts=local(candidate);
    if(parts.date===date&&parts.hour==='00'&&parts.minute==='00'&&parts.second==='00')candidates.add(candidate);
  }
  if(!candidates.size)throw new ReceiptSyncBoundaryError('native_import_identity_unavailable');
  return Math.min(...candidates);
}
async function incoming(snapshot: AppleEventSnapshot, owner: string) {
  const start=snapshot.all_day?midnight(snapshot.start,snapshot.effective_time_zone):Date.parse(snapshot.start);
  const end=snapshot.all_day?midnight(snapshot.end,snapshot.effective_time_zone):Date.parse(snapshot.end);
  if(!Number.isFinite(start)||!Number.isFinite(end)||end<=start)throw new ReceiptSyncBoundaryError('native_import_identity_unavailable');
  // Keep compatibility with appleCalendarSync's existing timed import mapping.
  // Native all-day semantics remain in the canonical receipt, not invented DB fields.
  const row={user_id:owner,source:'apple_calendar',external_provider:'apple',external_id:snapshot.id+':'+start,
    title:snapshot.title||'(no title)',tag:'Meeting',start_at:new Date(start).toISOString(),end_at:new Date(end).toISOString(),
    location:snapshot.location||null,notes:snapshot.notes||null,all_day_start:null,all_day_end:null,deleted_at:null};
  const hash=await fingerprint(row);
  return {row:{...row,provider_etag:BASELINE+JSON.stringify({modified:null,hash})},hash};
}
function validRow(value:unknown,owner:string,key:string):value is ImportedRow {
  return appleObject(value)&&appleUuid(value.id)&&value.user_id===owner&&value.source==='apple_calendar'&&
    value.external_provider==='apple'&&value.external_id===key&&typeof value.title==='string'&&typeof value.tag==='string'&&
    typeof value.start_at==='string'&&Number.isFinite(Date.parse(value.start_at))&&
    (value.end_at===null||typeof value.end_at==='string'&&Number.isFinite(Date.parse(value.end_at)))&&
    ['location','notes','all_day_start','all_day_end','provider_etag','deleted_at'].every(k=>value[k]===null||typeof value[k]==='string');
}

/** Reconciles only the canonical, device-reported receipt's exact target.
 * It never reads or mutates EventKit, never dispatches again, and never treats a
 * bounded calendar scan as proof of this target's reconciliation. All writes use
 * observed imported baselines and exact-field CAS; local changes stay intact. */
export async function reconcileAppleCalendarReceipt(options:AppleCalendarReceiptSyncOptions):Promise<AppleCalendarReceiptSyncResult> {
  const {client,assertCurrent,isCurrent}=options;
  // Awaited checks must retain the exact supplied receipt, not mutable UI refs.
  let review:AppleCalendarSealedReview,proof:AppleCalendarProof,currentActor:AppleCalendarActor;
  try{review=structuredClone(options.review);proof=structuredClone(options.proof);currentActor=structuredClone(options.currentActor);}
  catch{return result('pending','receipt_binding_unconfirmed');}
  const owner=currentActor?.owner_id;
  const check=()=>{if(!isCurrent())throw new ReceiptSyncBoundaryError('stale_calendar_request');};
  const current=async()=>{check();await assertCurrent();check();};
  const bound=async()=>{
    await current();
    const reply=await client.from('user_connections').select('id,user_id,provider,status,scope,provider_metadata,updated_at')
      .eq('user_id',owner).eq('provider','apple_calendar').eq('id',currentActor.connection_id).maybeSingle();
    await current();
    const c=reply.data;
    if(reply.error||!c||c.id!==currentActor.connection_id||c.user_id!==owner||c.provider!=='apple_calendar'||c.status!=='active')
      throw new ReceiptSyncBoundaryError('connection_changed');
    const generation=await connectorRequestHash({id:c.id,status:c.status,scope:c.scope,provider_metadata:c.provider_metadata,updated_at:c.updated_at});
    await current();
    if(generation!==currentActor.connection_generation)throw new ReceiptSyncBoundaryError('connection_changed');
  };
  const io=async<T>(operation:()=>PromiseLike<T>):Promise<T>=>{
    await bound();const reply=await operation();await bound();return reply;
  };
  try {
    check();
    if(!isAppleCalendarActor(currentActor)||!isAppleCalendarActor(review?.actor)||
      !validateAppleCalendarProof(proof,review,proof?.receipt_id)||currentActor.owner_id!==review.actor.owner_id||
      currentActor.device_id!==review.actor.device_id||currentActor.connection_id!==review.actor.connection_id||
      currentActor.connection_generation!==review.actor.connection_generation||
      (!options.allowHistoricalRecovery&&currentActor.auth_session_id!==review.actor.auth_session_id))
      return result('pending','receipt_binding_unconfirmed');

    // Receipt SELECT is owner-only under existing RLS. Its generated table types
    // lag the existing schema, so the cast is confined to this read.
    const receiptClient=client as unknown as SupabaseClient;
    const canonical=await io(()=>receiptClient.from('connector_action_receipts')
      .select('id,user_id,provider,capability,idempotency_key,status,result')
      .eq('id',proof.receipt_id).eq('user_id',owner).eq('provider','apple_calendar')
      .eq('capability',proof.action).eq('idempotency_key',proof.idempotency_key).eq('status','succeeded').maybeSingle());
    const receipt=canonical.data;
    if(canonical.error||!appleObject(receipt)||receipt.id!==proof.receipt_id||receipt.user_id!==owner||
      receipt.provider!=='apple_calendar'||receipt.capability!==proof.action||receipt.idempotency_key!==proof.idempotency_key||
      receipt.status!=='succeeded'||!appleObject(receipt.result)||receipt.result.request_hash!==review.request_hash||
      !appleObject(receipt.result.review)||!validateAppleCalendarProof(receipt.result.proof,review,proof.receipt_id))
      return result('pending','canonical_receipt_unconfirmed');
    const suppliedHash=await connectorRequestHash({review,proof});
    await current();
    const storedHash=await connectorRequestHash({review:receipt.result.review,proof:receipt.result.proof});
    await current();
    if(suppliedHash!==storedHash)return result('pending','canonical_receipt_unconfirmed');

    const before=proof.before?await incoming(proof.before,owner):null;await current();
    const after=proof.after?await incoming(proof.after,owner):null;await current();
    const read=async(key:string)=>{
      const reply=await io(()=>client.from('agenda_events').select(SELECT).eq('user_id',owner)
        .eq('source','apple_calendar').eq('external_provider','apple').eq('external_id',key).limit(2));
      if(reply.error||!Array.isArray(reply.data)||reply.data.length>1||
        reply.data.some(value=>!validRow(value,owner,key)))throw new ReceiptSyncBoundaryError('target_read_unconfirmed');
      return (reply.data[0]??null) as ImportedRow|null;
    };
    const unchanged=async(row:ImportedRow,hash:string)=>{
      const actual=await fingerprint(row);await current();
      return baselineHash(row.provider_etag)===hash&&actual===hash;
    };
    const mutate=async(row:ImportedRow,patch:Database['public']['Tables']['agenda_events']['Update'])=>{
      const reply=await io(()=>{
        let query=client.from('agenda_events').update(patch).eq('user_id',owner).eq('source','apple_calendar')
          .eq('external_provider','apple').eq('id',row.id).eq('external_id',row.external_id!)
          .eq('title',row.title).eq('tag',row.tag).eq('start_at',row.start_at);
        for(const column of ['end_at','location','notes','all_day_start','all_day_end','provider_etag','deleted_at'] as const)
          query=row[column]===null?query.is(column,null):query.eq(column,row[column]!);
        return query.select(SELECT);
      });
      if(reply.error)throw new ReceiptSyncBoundaryError('target_write_unconfirmed');
      if(!Array.isArray(reply.data)||reply.data.length!==1||reply.data[0].id!==row.id)return false;
      const verified=await read(row.external_id!);
      if(!verified||Object.entries(patch).some(([key,value])=>
        ['start_at','end_at','deleted_at'].includes(key)&&typeof value==='string'&&typeof verified[key as keyof ImportedRow]==='string'
          ?Date.parse(String(verified[key as keyof ImportedRow]))!==Date.parse(value)
          :verified[key as keyof ImportedRow]!==value))
        throw new ReceiptSyncBoundaryError('target_write_unconfirmed');
      return true;
    };

    const old=before?await read(before.row.external_id):null;
    // A same-key update already imported after this receipt is idempotently done.
    if(old&&after&&before?.row.external_id===after.row.external_id&&old.deleted_at===null&&await unchanged(old,after.hash))
      return result('ok','target_already_reconciled');
    if(before){
      if(!old)return result('pending','imported_before_missing');
      if(!await unchanged(old,before.hash))return result('conflict','local_or_imported_change_preserved');
      if(old.deleted_at!==null){
        if(proof.deleted)return result('ok','target_already_deleted');
        // A previous partial move can have tombstoned the old occurrence only
        // after inserting the verified new key. Ordinary tombstones stay closed.
        if(after&&after.row.external_id!==before.row.external_id){
          const target=await read(after.row.external_id);
          if(target&&target.deleted_at===null&&await unchanged(target,after.hash))return result('ok','target_already_reconciled');
        }
        return result('conflict','local_tombstone_preserved');
      }
    }
    if(proof.deleted){
      if(!old)return result('pending','imported_before_missing');
      const deletedAt=new Date(options.now??Date.now()).toISOString();
      return await mutate(old,{deleted_at:deletedAt})?result('ok','target_deleted'):result('conflict','target_changed_during_write');
    }
    if(!after)return result('pending','native_after_missing');
    if(old&&before?.row.external_id===after.row.external_id){
      return await mutate(old,after.row)?result('ok','target_updated'):result('conflict','target_changed_during_write');
    }

    let target=await read(after.row.external_id);
    if(target){
      if(target.deleted_at!==null)return result('conflict','local_tombstone_preserved');
      if(!await unchanged(target,after.hash))return result('conflict','after_target_change_preserved');
    }else{
      // Ignore duplicates protects a tombstone/local edit inserted after our read.
      const reply=await io(()=>client.from('agenda_events').upsert(after.row,{onConflict:'user_id,source,external_id',ignoreDuplicates:true}).select('id,external_id'));
      if(reply.error||!Array.isArray(reply.data)||reply.data.length>1||
        reply.data.some(value=>!appleUuid(value.id)||value.external_id!==after.row.external_id))
        return result('pending','target_insert_unconfirmed');
      target=await read(after.row.external_id);
      if(!target)return result('pending','target_insert_unconfirmed');
      if(target.deleted_at!==null)return result('conflict','local_tombstone_preserved');
      if(!await unchanged(target,after.hash))return result('conflict','after_target_change_preserved');
    }
    // A changed EventKit identifier or moved start is still the original reviewed
    // occurrence. Remove that exact old key only after verifying the new target.
    if(old){
      if(!await mutate(old,{deleted_at:new Date(options.now??Date.now()).toISOString()}))
        return result('conflict','target_changed_during_write');
      // The two keys are not a DB transaction: never publish a complete move
      // if the newly inserted instance changed while retiring the old key.
      const latest=await read(after.row.external_id);
      if(!latest||latest.deleted_at!==null||!await unchanged(latest,after.hash))
        return result('conflict','after_target_change_preserved');
    }
    return result('ok',old?'target_moved':'target_created');
  }catch(error){
    return result('pending',error instanceof ReceiptSyncBoundaryError?error.message:'target_reconciliation_unavailable');
  }
}
